import { useTranslation } from 'react-i18next';
import orderBy from 'lodash/fp/orderBy';
import CreateIcon from '@mui/icons-material/Create';
import { styled } from '@mui/material/styles';

import { EducationTalent } from '@bloomays-lib/types.shared';
import { constructDateDetails } from '@bloomays-lib/utils.shared';

import TalentSectionContainer from './TalentSectionContainer';
import SingleLineText, { SingleLineTextProps } from '../atoms/SingleLineText';
import CardItem from '../atoms/CardItem';

export type TalentFormationsSectionProps = {
  educations: EducationTalent[];
  addActionFn?: () => void;
  setOpenEduc?: (openEduc: { item: EducationTalent; show: boolean }) => void;
};

const TalentFormationsSection = ({ addActionFn, educations, setOpenEduc }: TalentFormationsSectionProps) => {
  const { t } = useTranslation(['talentSheet']);
  return (
    <TalentSectionContainer
      title={t('school')}
      emptyText={t('noEducation')}
      addActionFn={addActionFn}
      addActionText={t('addEducation', { ns: 'talentSheet' })}
    >
      {educations && educations.length > 0 && (
        <ContentWrapper>
          {orderBy(
            (item: EducationTalent) => item?.period?.startDate,
            ['desc'],
          )(educations)?.map((education, i) => {
            return (
              <Formation key={education.id}>
                {setOpenEduc && (
                  <StyledRowContainerAdd
                    onClick={() => {
                      setOpenEduc({
                        item: education,
                        show: true,
                      });
                    }}
                  >
                    <CreateIcon />
                  </StyledRowContainerAdd>
                )}
                <StyledContainerXP>
                  <CardItem
                    variant={'blue'}
                    size={'medium'}
                    emoji={<StyledSpan>{education?.name?.substring(0, 1)}</StyledSpan>}
                  />
                  <StyledContainerXPDetails>
                    <StyledTitles variant={'heading6'} text={education.label} />
                    <StyledTitles variant={'subtitle2Medium'} text={education?.name} />
                    <StyledDateText
                      variant={'subtitle1'}
                      text={constructDateDetails(education.period, education?.current)}
                    />
                    <SingleLineText variant={'body1Light'} text={education?.description} />
                  </StyledContainerXPDetails>
                </StyledContainerXP>
                {educations?.length - 1 !== i && <StyledBarXP></StyledBarXP>}
              </Formation>
            );
          })}
        </ContentWrapper>
      )}
    </TalentSectionContainer>
  );
};

export { TalentFormationsSection };
export default TalentFormationsSection;

const ContentWrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}));

const Formation = styled('div')(
  () => `
  position: relative;
  padding: 12px 0;
  `,
);

const StyledContainerXP = styled('div')(
  () => `
  display: flex;
  flex-flow: row nowrap;
  gap: 24px;
  align-items: flex-start;
  `,
);

const StyledRowContainerAdd = styled('div')(
  ({ theme }) => `
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  justify-content: flex-end;
  text-decoration: underline;
  color: ${theme.palette.neutral[80]};
  float: right;

  &:hover {
    cursor: pointer;
  }
  `,
);

const StyledDateText: React.FC<SingleLineTextProps> = styled(SingleLineText)(
  ({ theme }) => `
  color: ${theme.palette.neutral[30]};
  `,
);

const StyledTitles: React.FC<SingleLineTextProps> = styled(SingleLineText)(
  ({ theme }) => `
  color: ${theme.palette.neutral[80]};
  `,
);

const StyledContainerXPDetails = styled('div')(
  () => `
  display: flex;
  flex-flow: column nowrap;
  text-align: left;
  max-width: 1280px;
  gap: 8px;
  `,
);

const StyledBarXP = styled('div')(
  ({ theme }) => `
  height: 0px;
  margin-top: 32px;
  border: 1px solid ${theme.palette.neutral[20]};
  `,
);

const StyledSpan = styled('span')(
  () => `
  font-size: 16px;
  `,
);
